import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Plus } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import Marquee from "@/components/site/Marquee";
import ContactForm from "@/components/site/ContactForm";
import { fadeUp, stagger, lineReveal, viewport, easeSig } from "@/lib/motion";
import { pillars, framework, whyUs, industries, clientProcess, faqs, helpTopics, insights } from "@/lib/content";

const heroLines = ["Hacemos crecer empresas", "con estrategia, tecnología", "y soluciones inteligentes."];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero" className="relative pt-40 md:pt-52 pb-20 md:pb-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="text-xs uppercase tracking-[0.25em] text-[#0B1F3A]/60 mb-8">
          Consultoría para empresas · Barcelona
        </motion.div>

        <h1 className="text-4xl md:text-6xl lg:text-7xl leading-[1.03] tracking-tight text-[#0B1F3A] max-w-5xl">
          {heroLines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span custom={i} variants={lineReveal} initial="hidden" animate="visible" className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <p className="text-lg md:text-xl text-gray-500 max-w-xl leading-relaxed">
            Analizamos tu empresa, detectamos oportunidades reales de mejora y te ayudamos a implantar soluciones que aumentan la rentabilidad, la eficiencia y el crecimiento de tu negocio.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] text-white px-8 py-4 font-medium transition-all hover:scale-[1.03] hover:shadow-xl">
              Solicitar un diagnóstico inicial <ArrowUpRight size={18} />
            </Link>
            <Link to="/methodology" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 rounded-full border border-[#0B1F3A] text-[#0B1F3A] px-8 py-4 font-medium transition-all hover:bg-[#0B1F3A] hover:text-white">
              Descubrir cómo trabajamos
            </Link>
          </div>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 1.1, ease: easeSig }}
        className="max-w-[1400px] mx-auto px-6 md:px-12 mt-16 md:mt-24">
        <div className="relative rounded-[2rem] overflow-hidden h-[46vh] md:h-[62vh]">
          <motion.img style={{ y, scale }} src="https://images.unsplash.com/photo-1606836379799-f88b03bc7039?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Oficina corporativa moderna" className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-[#0B1F3A]/10" />
        </div>
      </motion.div>
    </section>
  );
};

const SectionHead = ({ overline, title, className = "" }) => (
  <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={viewport} className={className}>
    <motion.div variants={fadeUp} className="text-xs uppercase tracking-[0.25em] text-[#0B1F3A]/60 mb-5">{overline}</motion.div>
    <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl lg:text-6xl text-[#0B1F3A] tracking-tight leading-[1.05] max-w-3xl">{title}</motion.h2>
  </motion.div>
);

const HelpTopics = () => (
  <section data-testid="help-topics" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-36">
    <SectionHead overline="Cuéntanos tu reto" title="¿En qué podemos ayudarte?" />
    <div className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
      {helpTopics.map((t, i) => {
        const Icon = t.icon;
        return (
          <motion.div key={t.id} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={viewport}>
            <Link to="/contact" data-testid={`help-${t.id}`}
              className="group block h-full rounded-[1.5rem] bg-[#F3F4F6] p-7 transition-all duration-500 hover:bg-[#0B1F3A]">
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center mb-8 transition-colors group-hover:bg-white/10">
                <Icon size={22} className="text-[#0B1F3A] transition-colors group-hover:text-white" />
              </div>
              <h3 className="text-lg font-serif text-[#0B1F3A] mb-2 transition-colors group-hover:text-white">{t.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed transition-colors group-hover:text-white/70">{t.desc}</p>
            </Link>
          </motion.div>
        );
      })}
    </div>
  </section>
);

const WhyUs = () => (
  <section data-testid="why-us" className="bg-[#F3F4F6]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-44">
      <SectionHead overline="¿Por qué Medite Group?" title="No damos consejos genéricos. Entendemos tu negocio." />
      <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-16">
        {whyUs.map((w) => (
          <motion.div key={w.n} variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewport}
            data-testid={`why-${w.n}`} className="flex gap-6 md:gap-10">
            <div className="text-2xl font-serif text-[#0B1F3A]/30 pt-1">{w.n}</div>
            <div>
              <h3 className="text-2xl font-serif text-[#0B1F3A] mb-3">{w.title}</h3>
              <p className="text-gray-600 leading-relaxed max-w-md">{w.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Framework = () => (
  <section data-testid="methodology" className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-44">
    <SectionHead overline="Nuestra metodología" title="Un método claro, probado en cada proyecto." />
    <p className="mt-6 text-lg text-gray-500 max-w-xl">Cinco pasos sencillos que convierten los retos de tu empresa en mejoras reales y medibles.</p>

    <div className="mt-20 md:mt-28 relative">
      <div className="hidden md:block absolute top-[42px] left-0 right-0 h-px bg-gray-200" />
      <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-6">
        {framework.map((f, i) => (
          <motion.div key={f.step} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={viewport}
            data-testid={`framework-${f.step}`} className="relative">
            <div className="relative mb-6 flex items-center">
              <span className="w-3.5 h-3.5 rounded-full bg-[#0B1F3A] ring-8 ring-white" />
            </div>
            <div className="text-sm font-serif text-[#0B1F3A]/40 mb-2">{f.step}</div>
            <h3 className="text-xl font-medium text-[#0B1F3A] mb-3">{f.name}</h3>
            <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
    <Link to="/methodology" data-testid="methodology-link" className="mt-14 inline-flex items-center gap-2 text-[#0B1F3A] font-medium group">
      Ver nuestra metodología <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
    </Link>
  </section>
);

const Pillars = () => (
  <section data-testid="pillars" className="bg-[#0B1F3A] text-white">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-44">
      <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={viewport}>
        <motion.div variants={fadeUp} className="text-xs uppercase tracking-[0.25em] text-white/50 mb-5">Nuestras áreas</motion.div>
        <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-tight leading-[1.05] max-w-3xl">Cuatro áreas, un único equipo a tu lado.</motion.h2>
      </motion.div>

      <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        {pillars.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.div key={p.id} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={viewport}
              data-testid={`pillar-${p.id}`}
              className="group rounded-[2rem] border border-white/10 p-8 lg:p-12 transition-all duration-500 hover:bg-white/[0.04]">
              <div className="flex items-start justify-between">
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-8">
                  <Icon size={24} className="text-white" />
                </div>
                <span className="text-xs uppercase tracking-[0.2em] text-white/40">{p.tagline}</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif mb-3">{p.title}</h3>
              <p className="text-white/60 leading-relaxed max-w-md">{p.desc}</p>
            </motion.div>
          );
        })}
      </div>
      <Link to="/services" data-testid="pillars-link" className="mt-14 inline-flex items-center gap-2 rounded-full bg-white text-[#0B1F3A] px-7 py-3.5 font-medium hover:scale-[1.03] transition-transform">
        Ver cómo te ayudamos <ArrowUpRight size={18} />
      </Link>
    </div>
  </section>
);

const Industries = () => (
  <section data-testid="industries" className="py-20 md:py-28">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-10">
      <div className="text-xs uppercase tracking-[0.25em] text-[#0B1F3A]/60">Sectores con los que trabajamos</div>
    </div>
    <Marquee items={industries} />
  </section>
);

const Process = () => (
  <section data-testid="process" className="bg-[#F3F4F6]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-44 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <SectionHead overline="Cómo trabajamos" title="De la primera charla a los resultados." />
      </div>
      <div className="lg:col-span-7 lg:col-start-6">
        {clientProcess.map((c, i) => (
          <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewport}
            data-testid={`process-${i}`} className="flex gap-8 py-8 border-b border-gray-200 first:border-t">
            <div className="text-sm font-serif text-[#0B1F3A]/40 pt-1">0{i + 1}</div>
            <div>
              <h3 className="text-2xl font-serif text-[#0B1F3A] mb-2">{c.k}</h3>
              <p className="text-gray-600 leading-relaxed max-w-xl">{c.v}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Insights = () => (
  <section data-testid="insights" className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-44">
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
      <SectionHead overline="Ideas para tu negocio" title="Business Insights" />
      <span className="text-sm text-gray-400">Próximamente</span>
    </div>
    <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
      {insights.map((a, i) => (
        <motion.article key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={viewport}
          data-testid={`insight-${i}`} className="rounded-[1.5rem] border border-gray-100 p-8 flex flex-col h-full">
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B1F3A]/50 mb-6">{a.category}</div>
          <h3 className="text-2xl font-serif text-[#0B1F3A] mb-4 leading-snug">{a.title}</h3>
          <p className="text-gray-600 leading-relaxed flex-1">{a.excerpt}</p>
          <span className="mt-8 text-sm text-gray-400">Artículo próximamente</span>
        </motion.article>
      ))}
    </div>
  </section>
);

const Cases = () => (
  <section data-testid="cases" className="bg-[#F3F4F6]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-36">
      <SectionHead overline="Casos de éxito y opiniones" title="Historias reales, muy pronto." />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[0, 1, 2].map((i) => (
          <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={viewport}
            data-testid={`case-placeholder-${i}`}
            className="rounded-[1.5rem] border border-dashed border-gray-300 bg-white/50 p-8 min-h-[180px] flex items-center justify-center text-center">
            <p className="text-gray-400 leading-relaxed">Estamos preparando los primeros casos y testimonios de empresas que confían en nosotros.</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Faq = () => (
  <section data-testid="faq" className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-44 grid grid-cols-1 lg:grid-cols-12 gap-12">
    <div className="lg:col-span-4">
      <SectionHead overline="Preguntas frecuentes" title="Tus dudas, resueltas." />
    </div>
    <div className="lg:col-span-7 lg:col-start-6">
      <Accordion type="single" collapsible className="w-full">
        {faqs.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`} className="border-b border-gray-200">
            <AccordionTrigger className="text-left text-lg md:text-xl font-serif text-[#0B1F3A] py-6 hover:no-underline [&>svg]:hidden group">
              <span className="flex-1">{f.q}</span>
              <Plus size={20} className="text-[#0B1F3A]/50 transition-transform duration-300 group-data-[state=open]:rotate-45" />
            </AccordionTrigger>
            <AccordionContent className="text-gray-600 leading-relaxed text-base pb-6 max-w-2xl">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

const ContactCta = () => (
  <section data-testid="home-contact" className="bg-[#0B1F3A] text-white">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-44 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
      <div className="lg:col-span-5">
        <div className="text-xs uppercase tracking-[0.25em] text-white/50 mb-5">Empecemos por conocernos</div>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-tight leading-[1.05]">Cuéntanos tu reto. Nosotros te decimos cómo resolverlo.</h2>
        <p className="mt-6 text-white/60 leading-relaxed max-w-md">Solicita un diagnóstico inicial sin compromiso. Te responderemos en un día laborable.</p>
      </div>
      <div className="lg:col-span-6 lg:col-start-7">
        <ContactForm />
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="home-page">
      <Hero />
      <HelpTopics />
      <WhyUs />
      <Framework />
      <Pillars />
      <Industries />
      <Process />
      <Insights />
      <Cases />
      <Faq />
      <ContactCta />
    </motion.div>
  );
}
